import BugIcon from "@mui/icons-material/BugReportOutlined";
import CheckIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorIcon from "@mui/icons-material/ErrorOutlineRounded";
import type { SelectChangeEvent } from "@mui/material";
import { CircularProgress, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";
import FileIcon from "@entrilia/shared/components/FileIcon";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { arraysHaveSameItems } from "@entrilia/shared/utilities/arrayHelper";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import FundraisingCategorySelect from "./FundraisingCategorySelect";
import FundraisingDocumentsContext from "./FundraisingDocumentsContext";
import type { FundraisingDocument } from "./fundraisingDocumentsState";
import {
  selectAccessOptions,
  updateDocumentsAccessOptionsAction,
  updateDocumentsCategoryAction,
} from "./fundraisingDocumentsState";

export const FileStatusOrIcon = ({ row }: { row: FundraisingDocument }) => {
  if (row.isDownloading) {
    return <CircularProgress size={20} color="secondary" sx={{ flexShrink: 0 }} />;
  }

  if (row.uploadStatus === "uploading") {
    return <CircularProgress size={20} color="secondary" sx={{ flexShrink: 0 }} />;
  }

  if (row.uploadStatus === "upload_completed") {
    return <CheckIcon color="success" />;
  }

  if (row.uploadStatus === "error") {
    if (row.validationError) {
      return (
        <Tooltip title={row.validationError} arrow>
          <ErrorIcon color="error" />
        </Tooltip>
      );
    }

    if (row.uploadError?.type === "MalwareDetected") {
      return (
        <Tooltip title="Upload failed: malware detected" arrow>
          <BugIcon color="error" />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Upload failed" arrow>
        <ErrorIcon color="error" />
      </Tooltip>
    );
  }

  return <FileIcon fileExtension={getFileExtension(row.name)} />;
};

export const FileNameCell = ({ row }: { row: FundraisingDocument }) => {
  const { onPreviewFile } = useDefinedContext(FundraisingDocumentsContext);

  const handleClick = () => {
    if (row.uploadStatus === undefined) {
      onPreviewFile(row.fileId);
    }
  };

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", width: "100%", pl: 5 }}>
      <FileStatusOrIcon row={row} />
      <Typography
        noWrap
        onClick={handleClick}
        color={row.uploadStatus === undefined ? "inherit" : "textSecondary"}
        sx={(t) =>
          row.uploadStatus === undefined ? { cursor: "pointer", "&:hover": { color: t.palette.primary.main } } : {}
        }
      >
        {row.name}
      </Typography>
    </Stack>
  );
};

export const CategorySelectCell = ({ row }: { row: FundraisingDocument }) => {
  const { documentsState, setDocumentsState } = useDefinedContext(FundraisingDocumentsContext);

  const handleChange = (externalCategoryId: string | undefined) => {
    setDocumentsState(updateDocumentsCategoryAction([row.fileId], externalCategoryId));
  };

  return (
    <FundraisingCategorySelect
      variant="standard"
      externalCategoryId={row.externalCategoryId}
      onChange={handleChange}
      disabled={documentsState.isSaving}
    />
  );
};

export const AccessOptionsSelect = ({ row }: { row: FundraisingDocument }) => {
  const { documentsState, setDocumentsState } = useDefinedContext(FundraisingDocumentsContext);

  const handleChange = (e: SelectChangeEvent) => {
    const newOptions = selectAccessOptions.find(([value]) => value === e.target.value)?.[1];
    if (newOptions !== undefined) {
      setDocumentsState(updateDocumentsAccessOptionsAction([row.fileId], newOptions));
    }
  };

  const defaultValue = "View & Download";
  const defaultOptions = ["View", "Download"];
  const rowValue =
    selectAccessOptions.find(([, options]) => arraysHaveSameItems(options, row.accessOptions ?? defaultOptions))?.[0] ??
    defaultValue;

  return (
    <Select
      variant="standard"
      disableUnderline
      disabled={documentsState.isSaving}
      value={rowValue}
      onChange={handleChange}
      sx={{ ".MuiInputBase-input": { "&:focus": { backgroundColor: "transparent" } } }}
    >
      {selectAccessOptions.map(([value]) => (
        <MenuItem key={value} value={value}>
          {value}
        </MenuItem>
      ))}
    </Select>
  );
};
